import type { TestGroupingGroup } from '@vedalib/editor/lib/elements'
import { FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import { generateId } from '@vedalib/editor/lib/utils/id'
import * as R from 'ramda'
import { useCallback, useEffect, useRef } from 'react'
import React from 'react'

import FloatingPanel, { PanelIconImage } from 'components/Editor/FloatingPanel'
import Padding from 'components/Editor/Toolbar/Padding'
import { IRenderItemProps } from 'components/uiKit/ArrayControl'
import { IconButton, TileButton } from 'components/uiKit/Button'
import { Tooltip } from 'components/uiKit/Dropdown'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import { Typography } from 'components/uiKit/Typography'
import { NOOP } from 'constants/commonConstans'
import { setElement } from 'services/Store/Project/actions'
import { useProjectContext, useProjectDispatch } from 'services/Store/Project/hooks'
import { getActiveElementId, getElementState } from 'services/Store/Project/selectors'
import { t } from 'services/Translation'
import { getFileByUsage } from 'utils/files'

import {
  useGroupingFormValueUpdateForm,
  useGroupingFormValueContext,
} from './TestGroupingFormValueContext'
import * as s from './TestGroupingFormValueItemGroup.module.scss'
import FloatingPanelMap from './TestGroupingFormValueItemGroupFloating'
import type { TestGroupingStateType } from './testGroupingTypes'

const TestGroupingFormValueGroupItem: React.FC<IRenderItemProps<TestGroupingGroup>> = ({
  name,
  item,
  index,
  drag,
  min,
  max,
  value: array,
  onChange,
}) => {
  const dispatch = useProjectDispatch()
  const elementId = useProjectContext(getActiveElementId)
  const elementState = useProjectContext(getElementState, elementId || '') as
    | TestGroupingStateType
    | undefined
  const disableClone = Boolean(max && array.length >= max)
  const disableDelete = Boolean(min && array.length <= min)
  const { form } = useGroupingFormValueContext()
  const setForm = useGroupingFormValueUpdateForm()
  const file = getFileByUsage(item.target.image, FileUsageTypeEnum.image)

  const setState = useCallback(
    (value: TestGroupingStateType) => {
      if (elementId) {
        dispatch(setElement({ id: elementId, value }))
      }
    },
    [dispatch, elementId],
  )

  const handleFocus = useCallback(
    () => setState({ activeGroup: item.value, activeAnswer: null }),
    [setState, item.value],
  )

  const handleBlur = useCallback(() => setState({ activeGroup: null }), [setState])

  const onClone = useCallback(
    () =>
      onChange?.([...array, { ...R.clone(array[index]), value: generateId() }], {
        command: 'clone',
        item: array[index],
      }),
    [onChange, array, index],
  )

  const onDelete = useCallback(
    () =>
      onChange?.(
        array.filter((_item, i) => i !== index),
        { command: 'delete', item: array[index] },
      ),
    [onChange, array, index],
  )

  const handleClose = useCallback(
    () => setForm({ form: 'root', groupIndex: index, groupName: name }),
    [index, name, setForm],
  )

  const setOpenRef = useRef<((open: boolean) => void) | null>(null)

  useEffect(() => {
    if (elementState?.activeGroup && elementState.activeGroup !== item.value) {
      setOpenRef.current?.(true)
    }
  }, [elementState?.activeGroup, item.value])

  const Header = (form && FloatingPanelMap[form].header) || NOOP
  const Panel = (form && FloatingPanelMap[form].panel) || NOOP

  return (
    <div className={s.root} onBlur={handleBlur} onFocus={handleFocus}>
      <div className={s.header} ref={drag}>
        <span className={s.title}>
          <Icon name='builderDrugAndDrop' size={KitSize.S} />
          <Typography rows={1}>
            {t('elements.grouping.tags.group')} {index + 1}
          </Typography>
        </span>
        <span className={s.actions}>
          <Tooltip overlay={t('uiKit.tooltip.duplicate')}>
            <IconButton
              disabled={disableClone}
              icon='otherCopy'
              name={`${name}.${index}.copy`}
              onClick={onClone}
              size={KitSize.XS}
              styleType='ghost'
            />
          </Tooltip>
          <Tooltip overlay={t('uiKit.tooltip.delete')}>
            <IconButton
              disabled={disableDelete}
              icon='otherTrash'
              name={`${name}.${index}.delete`}
              onClick={onDelete}
              size={KitSize.XS}
              styleType='ghost'
            />
          </Tooltip>
        </span>
      </div>
      <Padding gap>
        <FloatingPanel
          header={<Header group={item} index={index} name={name} />}
          onClose={handleClose}
          panel={<Panel group={item} index={index} name={name} />}
        >
          {({ open, setOpen }) => {
            setOpenRef.current = setOpen
            return (
              <TileButton
                active={open}
                icon='otherAdd'
                name={`${name}.${index}.open`}
                styleType='ghost'
              >
                <PanelIconImage file={item.target.image} icon='otherBlock' />
                <Typography rows={1} styleType='hint'>
                  {file?.name || t('elements.grouping.tags.group')}
                </Typography>
              </TileButton>
            )
          }}
        </FloatingPanel>
      </Padding>
    </div>
  )
}

export default React.memo(TestGroupingFormValueGroupItem) as typeof TestGroupingFormValueGroupItem
